import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Button,
  Card,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Box from "@mui/material/Box";
import SearchBar from "../../components/SearchBar";
import { useNavigate } from "react-router-dom";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import { isValidInput } from "../../utils/validations";
import {
  useChangeSubscriptionStatusMutation,
  useDeleteSubscriptionMutation,
  useLazyGetAllSubscriptionsQuery,
} from "../../services/subscription";
import { CommonBody, Subscription } from "../../types/General";
import { showError, showToast, Loader } from "../../constants";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import SkeletonTable from "../../components/SkeletonTable";
import WarnModal from "../../components/WarnModal";
import useAuth from "../../hooks/useAuth";
import useTranslation from "../../hooks/Translation";
import Pagination from "../../components/Pagination";
import NoDataFound from "../../components/NoDataFound";
import {
  PERMISSION_MODULES,
  canShowTableActions,
  getPagePermissionFlags,
} from "../../constants/permissionModules";

const ManageSubscription = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [getSubscriptionsData, { isLoading, isFetching, isSuccess }] =
    useLazyGetAllSubscriptionsQuery();
  const [subscriptionStatus] = useChangeSubscriptionStatusMutation();
  const [deleteSubscription] = useDeleteSubscriptionMutation();

  const [totalCount, setTotalCount] = useState<number>(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [subsData, setSubsData] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [hidePermission, setHidePermission] = useState<any>({
    isAdd: false,
    isDelete: false,
    isView: true,
  });
  const translate = useTranslation();
  
  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  let totalPages = Math.ceil(totalCount / 10);
  
  const getSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await getSubscriptionsData({
        query: debouncedSearchTerm.trim(),
        page: page,
      }).unwrap();
      if (response?.statusCode === 200) {
        setSubsData(response?.data?.membership);
        setTotalCount(response?.data?.totalCount || response?.data?.count || response?.data?.totalPages || 0);
      } else {
        setSubsData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setSubsData([]);
    } finally {
      setLoading(false);
    }
  };

  const checkPermission = () => {
    setHidePermission(
      getPagePermissionFlags(
        userData?.permission,
        PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS,
        userData?.role === "ADMIN",
      ),
    );
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteSubscription({ subsId: id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(translate?.error?.subDel || "");
        getSubscriptions();
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const updateUserStatus = async (id: string, status: boolean) => {
    const data = {
      isBlocked: status,
    };

    try {
      let encryptedBody = generateEncryptedKeyBody(data) as CommonBody;

      const response = await subscriptionStatus({
        encryptedBody,
        subsId: id,
      }).unwrap();
      if (response?.statusCode === 200) {
        const updatedData = subsData?.map((x: any) => {
          if (x._id === id) {
            return {
              ...x,
              isBlocked: status,
            };
          }
          return x;
        });
        setSubsData(updatedData);
        showToast(translate?.error?.statusUpdate || "");
      } else {
        showError(translate?.error?.pleaseTryAgain || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || translate?.error?.pleaseTryAgain || "");
    }
  };

  const formatNumberWithCommas = (number: any) => {
    const value = Number(number);
    if (!Number.isFinite(value)) return "-";
    return `${value.toLocaleString("en-IN")} KD`;
  };


  const label = { inputProps: { "aria-label": "Switch demo" } };

  useEffect(() => {
    if (userData) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    getSubscriptions();
  }, [page, debouncedSearchTerm]);

  return (
    <MainContainer>
      <Loader isLoad={loading} isLinear />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng"> {translate?.main?.ManageSubscriptionPlan}</h1>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <Box className="cards_header_right">
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-subscription/purchases")}
                sx={{ mr: 1 }}
              >
                {translate?.main?.PurchasedSubscriptions ||
                  "Purchased Subscriptions"}
              </Button>
              {hidePermission?.isAdd ? (
                <Button
                  className="btn btn_primary"
                  onClick={() => navigate("/manage-subscription/add")}
                >
                  {translate?.main?.AddPlan}
                </Button>
              ) : null}
            </Box>
          </Box>
          <TableContainer className="table_container">
            <Box className="heading"></Box>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{translate?.main?.SNo}</TableCell>
                  <TableCell>{translate?.main?.PlanName}</TableCell>
                  <TableCell>{translate?.main?.Validity}</TableCell>
                  <TableCell>{translate?.main?.Price}</TableCell>
                  <TableCell>{translate?.main?.Description}</TableCell>
                  {hidePermission?.isAdd ? <TableCell>{translate?.main?.Status}</TableCell> : null}
                  {(hidePermission?.isAdd || hidePermission?.isDelete) ? <TableCell>{translate?.main?.Action}</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonTable columns={7} rows={10} />
                ) : subsData?.length > 0 ? (
                  subsData?.map((row, i) => (
                    <TableRow key={row?._id}>
                      <TableCell align="center">{(page - 1) * 10 + i + 1}</TableCell>
                      <TableCell>{row?.planName || "-"}</TableCell>
                      <TableCell>
                        {row?.validity
                          ? (() => {
                              const unit = row?.validityUnit;
                              const isOne = Number(row?.validity) === 1;
                              const unitLabel =
                                unit === "minutes"
                                  ? isOne
                                    ? translate?.main?.Minute || "Minute"
                                    : translate?.main?.Minutes || "Minutes"
                                  : unit === "hours"
                                    ? isOne
                                      ? translate?.main?.Hour || "Hour"
                                      : translate?.main?.Hours || "Hours"
                                    : unit === "days"
                                      ? isOne
                                        ? translate?.main?.Day || "Day"
                                        : translate?.main?.Days || "Days"
                                      : isOne
                                        ? translate?.main?.Month
                                        : translate?.main?.Months;
                              return `${row.validity} ${unitLabel}`;
                            })()
                          : "-"}
                      </TableCell>
                      <TableCell>{formatNumberWithCommas(row?.price)}</TableCell>
                      <TableCell>{row?.description || "-"}</TableCell>
                      {hidePermission?.isAdd ? (
                        <TableCell>
                          <Switch
                            {...label}
                            size="small"
                            checked={!row?.isBlocked}
                            onChange={() =>
                              updateUserStatus(row?._id, !row?.isBlocked)
                            }
                          />
                        </TableCell>
                      ) : null}
                      {(hidePermission?.isAdd || hidePermission?.isDelete) ? (
                        <TableCell>
                          <Box className="table_actions">
                            {hidePermission?.isAdd ? (
                              <IconButton
                                onClick={() =>
                                  navigate(
                                    `/manage-subscription/edit/${row?._id}`
                                  )
                                }
                              >
                                <ModeEditIcon />
                              </IconButton>
                            ) : null}
                            {hidePermission?.isDelete ? (
                              <IconButton
                                onClick={() => {
                                  setOpen(true);
                                  setSelectedId(row?._id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : null}
                          </Box>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <NoDataFound message="No Subscription Plan Found" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {subsData?.length > 0 ? (
          <Pagination
            setPage={setPage}
            module={subsData}
            page={page}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        ) : (
          ""
        )}
      </div>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => handleDelete(selectedId)}
        name="Subscription"
      />
    </MainContainer>
  );
};

export default ManageSubscription;
